import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { NetworkStatus } from '../LoadingStates';
import { useAuth } from '../../contexts/AuthContext';

const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.primary[600]};
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const TitleSection = styled.div`
  flex: 1;
  min-width: 0; /* Allow text truncation */
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.inverse};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary[100]};
  margin: 0;
  margin-top: ${({ theme }) => theme.spacing[1]};
  display: none;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: block;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-shrink: 0;
`;

const SystemStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const StatusIndicator = styled.div<{ status: 'online' | 'offline' | 'processing' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ status, theme }) => {
    switch (status) {
      case 'online':
        return theme.colors.success[400];
      case 'processing':
        return theme.colors.warning[400];
      case 'offline':
        return theme.colors.error[400];
      default:
        return theme.colors.gray[400];
    }
  }};
  flex-shrink: 0;
`;

const StatusText = styled.span`
  display: none;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: inline;
  }
`;

const NetworkStatusWrapper = styled.div`
  display: none;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-left: ${({ theme }) => theme.spacing[3]};
  border-left: 1px solid ${({ theme }) => theme.colors.primary[500]};
`;

const UserInfo = styled.div`
  display: none;
  flex-direction: column;
  align-items: flex-end;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: flex;
  }
`;

const UserName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.inverse};
`;

const UserRole = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary[100]};
  text-transform: uppercase;
`;

const LogoutButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary[700]};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary[800]};
  }
  
  &:active {
    background-color: ${({ theme }) => theme.colors.primary[900]};
  }
`;

export const Header: React.FC = () => {
  const isOnline = useOnlineStatus();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  console.log('[Header] Current user:', user);
  console.log('[Header] Is admin:', user?.role === 'admin');
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  return (
    <HeaderContainer>
      <HeaderContent>
        <TitleSection>
          <Title>Drawing Score Evaluator</Title>
          <Subtitle>AI-Powered Drawing Assessment System</Subtitle>
        </TitleSection>
        
        <StatusSection>
          <SystemStatus>
            <StatusIndicator status={isOnline ? 'online' : 'offline'} />
            <StatusText>
              {isOnline ? 'System Online' : 'System Offline'}
            </StatusText>
          </SystemStatus>
          
          <NetworkStatusWrapper>
            <NetworkStatus isOnline={isOnline} />
          </NetworkStatusWrapper>
          
          {user && (
            <UserSection>
              <UserInfo>
                <UserName>{user.fullName || user.username}</UserName>
                <UserRole>{user.role}</UserRole>
              </UserInfo>
              <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
            </UserSection>
          )}
        </StatusSection>
      </HeaderContent>
    </HeaderContainer>
  );
};